/* eslint-disable no-param-reassign */
import * as tt from '@tomtom-international/web-sdk-services/dist/services.min';

import {
    boundingRect,
    panoramaForRect,
    titleResult,
    addressResults,
} from '../helpers';

/**
 * Radius (in meters) of area searched for panorama points when user
 * clicks on the map.
 */
const RADIUS = 50;

/**
 * Minimum height of panorama and map panes, in pixels.
 */
const MIN_PANE_HEIGHT = 300;

/**
 * Initial values for camera orientation.
 */
const INITIAL_ORIENTATION = {
    yaw: 0.0,
    pitch: 0.0,
    fov: 55,
};

const { services: { reverseGeocode } } = tt;

/**
 * Store module with functionalities specific for MoMa Panorama View.
 * It provides info about currently selected point and state
 * of the split panes.
 */
/**
 * All hew state elements added to this module should also be added
 * to the validation schema at /src/store/storage/schema.js
 */

/**
 * Names of mutations further used by their variable names, not values.
 * E.g. first mutation is referred by `[SET_SPLITPANES_SIZE]`,
 * not `'setSplitpanesSize'`.
 */
const mutationNames = {
    SET_SPLITPANES_SIZE: 'setSplitpanesSize',
    SET_CONTAINER_HEIGHT: 'setContainerHeight',
    SET_CURRENT_PANORAMA: 'setCurrentPanorama',
    SET_REVERSE_GEOCODE: 'setReverseGeocode',
    SET_LOADING_STATUS: 'setLoadingStatus',
    SET_ORIENTATION: 'setOrientation',
    TOGGLE_INFORMATION_VISIBLE: 'toggleInformationVisible',
    HIDE_PANORAMA_INFORMATION: 'hideInformationVisible',
    SET_GAMMA: 'setGamma',
};

const mutations = {
    /**
     * Splitpanes size setter mutation.
     * @param {number} size First pane size, in percent of window height.
     */
    [mutationNames.SET_SPLITPANES_SIZE]: (state, size) => {
        state.splitpanesSize = size;
    },

    /**
     * Panes container height setter mutation.
     * @param {number} height Split panes container height, in pixels.
     */
    [mutationNames.SET_CONTAINER_HEIGHT]: (state, height) => {
        state.containerHeight = height;
    },

    /**
     * Current panorama setter mutation.
     * @param {Object} panorama Panorama object. Contains data about
     * currently selected point and nearest panorama objects.
     */
    [mutationNames.SET_CURRENT_PANORAMA]: (state, panorama) => {
        state.panorama = panorama;
    },

    /**
     * Reverse geocoding for current panorama setter mutation.
     * @param {Object} rgc Revese Geocoding object.
     */
    [mutationNames.SET_REVERSE_GEOCODE]: (state, rgc) => {
        state.rawReverseGeocode = rgc;
    },

    /**
     * Loading status flag setter mutation.
     * @param {boolean} loading
     */
    [mutationNames.SET_LOADING_STATUS]: (state, loading) => {
        state.isLoading = loading;
    },

    /**
     * Camera orientation setter mutation.
     * @param {Object} orientation
     * @param {number} orientation.yaw Camera yaw, float.
     * @param {number} orientation.pitch Camera pitch, float.
     */
    [mutationNames.SET_ORIENTATION]: (state, orientation) => {
        state.orientation = orientation;
    },

    /**
     * Toogle panorama details flag for hide/show information.
     */
    [mutationNames.TOGGLE_INFORMATION_VISIBLE]: (state) => {
        state.informationVisible = !state.informationVisible;
    },

    /**
     * Hide panorama details on mounted to details.
     */
    [mutationNames.HIDE_PANORAMA_INFORMATION]: (state) => {
        state.informationVisible = false;
    },

    /**
     * Saves gamma correction factor into the store.
     * @param {number} gamma - The new gamma correction factor.
     */
    [mutationNames.SET_GAMMA]: (state, gamma) => {
        state.gamma = gamma;
    },
};

const state = {
    /**
     * Panorama state object. Contains data about
     * currently selected point and nearest panorama objects.
     * @type {Object}
     */
    panorama: {},

    /**
     * Flag determining whether the panorama is currently loading.
     * @type {boolean}
     */
    isLoading: true,

    /**
     * Vertical size of first one
     * out of two split panes, in percent.
     * @type {number}
     */
    splitpanesSize: 75,

    /**
     * Height of the split panes container, in pixels.
     * @type {number}
     */
    containerHeight: 0,

    /**
     * Reverse geocoding data for current panorama location.
     * @type {?Object}
     */
    rawReverseGeocode: null,

    /**
     * Camera orientation.
     * @type {?Object} orientation
     * @type {number} orientation.yaw Camera yaw, float.
     * @type {number} orientation.pitch Camera pitch, float.
     */
    orientation: { ...INITIAL_ORIENTATION },

    /**
     * Flag to control of show/hide panorama details information.
     * @type {Boolean}
     */
    informationVisible: false,

    /**
     * Contains gamma correction factor for the panorama. Should be
     * within `<-1, 1>` range.
     * @type {Number}
     */
    gamma: 0,
};

const getters = {
    /**
     * Central point of panorama, point on map
     * where panorama is placed. Returned as object
     * with latitude & longitude coordinates.
     * @type {Object}
     */
    panoramaCenter: ({ panorama: { selected: { lat, lng } = {} } }) => ({
        lat, lng,
    }),

    /**
     * Indicates if there is reverse geocoding data.
     * @type {boolean}
     */
    hasReveseGeocode: ({ rawReverseGeocode }) => !!rawReverseGeocode,

    /**
     * Details for reverse geocoding result.
     * Contains title, address and coordinates.
     * When there is no data, empty object is returned.
     * @type {Object}
     */
    reverseGeocodeDetails: ({ rawReverseGeocode }, { hasReveseGeocode }) => {
        if (!hasReveseGeocode) {
            return {};
        }

        const {
            addresses: [selection],
        } = rawReverseGeocode;

        const {
            position: { lat, lng } = {},
        } = selection;

        return {
            title: titleResult(selection),
            detailAddress: addressResults(selection),
            coordinates: `${lat}, ${lng}`,
        };
    },

    /**
     * Minimum height of a split pane to allow for MIN_PANE_HEIGHT,
     * in percent. Will default to 50 if the container is too small.
     * @type {number}
     */
    minPaneSize: ({ containerHeight }) => (
        Math.min(50, Math.ceil(MIN_PANE_HEIGHT * 100 / containerHeight))
    ),

    /**
     * Selected session name for panorama position details. Return string with full
     * data of session name.
     * @type {string}
     */
    selectedSessionName: ({ panorama: { selected: { sessionName } = {} } }) => sessionName,


    /**
     * Selected sequence number for panorama position details. Return string with
     * full data of sequence number.
     * @type {string}
     */
    selectedSequenceNumber: ({ panorama: { selected: { sequenceNumber } = {} } }) => sequenceNumber,

    /**
     * Selected coordinates for panorama position details. Return string with full
     * data of selected coordinates.
     * @type {string}
     */
    selectedCoordinates: ({ panorama: { selected: { lng, lat } = {} } }) => `${lat}, ${lng}`,

    /**
     * Parse date from sessionName to human readable version.
     * @type {string}
     */
    selectedDate: (_, { selectedSessionName }) => {
        if (!selectedSessionName) {
            return null;
        }

        return {
            date: selectedSessionName.split('_').splice(1, 3).join('-'),
        };
    },

    /**
     * Parse time from sessionName to human readable version.
     * @type {string}
     */
    selectedTime: (_, { selectedSessionName }) => {
        if (!selectedSessionName) {
            return null;
        }

        return {
            time: selectedSessionName.split('_').splice(5, 6).join(':'),
        };
    },
};

const actions = {
    /**
     * Converts given coordinates to a bounded rectangle which is
     * centered around given point. It should dispatch
     * getClosestPanorama action upon completion.
     * @param {Object} context - Vuex context.
     * @param {Object} coordinates - Coordinates of central point.
     * @param {number} coordinates.lng - Longitude of center point.
     * @param {number} coordinates.lat - Latitude of center point.
     */
    selectCoordinates: ({ dispatch }, { lng, lat }) => {
        dispatch('getClosestPanorama', boundingRect({ lng, lat }, RADIUS));
    },

    /**
     * Contacts the MoMa API to obtain closest panorama point to the
     * center of given rectangle.
     * @param {Object} context - Vuex context.
     * @param {Array} rectangle - Array containing coordinates of
     *     bounding box rectangle.
     * @param {number} rectangle[0] - Longitude of south-west corner of
     *     bounding box.
     * @param {number} rectangle[1] - Latitude of south-west corner of
     *     bounding box.
     * @param {number} rectangle[2] - Longitude of north-east corner of
     *     bounding box.
     * @param {number} rectangle[3] - Latitude of north-east corner of
     *     bounding box.
     */
    getClosestPanorama: async (
        { commit, rootState: { apiKey: key }, rootGetters: { momaURL } },
        boundingBox,
    ) => {
        const panorama = await panoramaForRect({
            boundingBox,
            urlBuilder: momaURL,
        });
        if (!panorama) {
            return;
        }

        commit(mutationNames.SET_CURRENT_PANORAMA, panorama);

        // Getters won't update mid-action so we need to extract panorama
        // coordinates manually.
        const { selected: { lat, lng } } = panorama;
        const position = { lat, lng };
        const rgc = await reverseGeocode({ key, position }).go();

        commit(mutationNames.SET_REVERSE_GEOCODE, rgc);
    },

    /**
    * Loading status flag setter action.
    * @param {boolean} loading
    */
    setLoadingStatus: ({ commit }, loading) => {
        commit(mutationNames.SET_LOADING_STATUS, loading);
    },

    /**
    * Splitpanes size setter action.
    * @param {number} size First pane size, in percent of window height.
    */
    setSplitpanesSize: ({ commit }, size) => {
        commit(mutationNames.SET_SPLITPANES_SIZE, size);
    },

    /**
    * Sets panes container height. Also updates pane size accordingly
    * if necessary.
    * @param {number} height Current container height, in pixels.
    */
    setContainerHeight: ({ commit, state: { splitpanesSize } }, height) => {
        commit(mutationNames.SET_CONTAINER_HEIGHT, height);

        const minSize = Math.min(50, Math.ceil(MIN_PANE_HEIGHT * 100 / height));
        if (splitpanesSize < minSize) {
            commit(mutationNames.SET_SPLITPANES_SIZE, minSize);
        } else if ((100 - splitpanesSize) < minSize) {
            commit(mutationNames.SET_SPLITPANES_SIZE, 100 - minSize);
        }
    },

    /**
     * Sets camera orientation.
     * @param {Object} orientation
     * @param {number} orientation.yaw Camera yaw, float.
     * @param {number} orientation.pitch Camera pitch, float.
     */
    setOrientation: ({ commit }, orientation) => {
        commit(mutationNames.SET_ORIENTATION, orientation);
    },

    /**
     * Resets camera orientation.
     */
    resetOrientation: ({ commit }) => {
        commit(mutationNames.SET_ORIENTATION, { ...INITIAL_ORIENTATION });
    },

    /**
     * Sets camera orientation to null.
     */
    clearOrientation: ({ commit }) => {
        commit(mutationNames.SET_ORIENTATION, null);
    },

    toggleInformationVisible: ({ commit }, informationVisible) => {
        commit(mutationNames.TOGGLE_INFORMATION_VISIBLE, { informationVisible });
    },

    hideInformationVisible: ({ commit }, informationVisible) => {
        commit(mutationNames.HIDE_PANORAMA_INFORMATION, { informationVisible });
    },

    /**
     * Sets gamma correction factor in the store.
     * @param {number} gamma - The gamma correction factor to be set. It
     *     will be clamped to `<-1, 1>` range. For NaN it will set 0.
     */
    setGamma: ({ commit }, gamma) => {
        const clampedGamma = Number.isNaN(gamma)
            ? 0
            : Math.min(1, Math.max(-1, gamma));
        commit(mutationNames.SET_GAMMA, clampedGamma);
    },
};

/**
 * Vuex store module object, created by joining
 * actions, getters,  and state objects.
 */
export default {
    namespaced: true,
    actions,
    getters,
    mutationNames,
    mutations,
    state,
};
